<script setup lang="ts">
import Password from 'primevue/password';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';

import type {
  InputPasswordEmits,
  InputPasswordProps,
} from './InputPassword.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';
import InputGroup from '../inputgroup/InputGroup.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<InputPasswordProps>(), {
  feedback: false,
});

const emit = defineEmits<InputPasswordEmits>();

const Preset = inject<Record<string, any>>('preset', {}).password;

const inputModel = ref<string | undefined>(props.modelValue);
const field = reactive<FieldValidation<typeof props.modelValue>>({
  value: undefined,
});

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

onMounted(() => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'password', (value: string) => {
        return nextTick(() => {
          return setValidatorMessage(value?.trim());
        }); // Waits props.invalid changed
      }),
    );

    if (props.modelValue != null) field.value = props.modelValue;
  }
});

const setValidatorMessage = (value: string): boolean | string => {
  if (typeof props.validatorMessage === 'string' && props.invalid) {
    return props.validatorMessage;
  } else if (typeof props.validatorMessage !== 'string') {
    const { empty } = props.validatorMessage ?? {};
    if (!value && props.mandatory) {
      return empty ?? true;
    }
  }

  return true;
};

const updateFieldValue = (event: string | undefined): void => {
  emit('update:modelValue', event);

  if (props.useValidator) field.value = event;
};

/**
 * Focusing input element after toggle mask.
 * @param e ClickEvent
 */
const focusInput = (e: MouseEvent): void => {
  // Ensure the target is an HTMLElement to access the previousElementSibling property
  const target = e.target as HTMLElement;

  // Check if the previous element sibling exists and is an input element
  if (
    target.previousElementSibling &&
    target.previousElementSibling.tagName === 'INPUT'
  ) {
    // Cast the previous element sibling to HTMLInputElement to call the focus method
    const input = target.previousElementSibling as HTMLInputElement;
    input.focus();
  }
};

/**
 * For async data, watch the changes of modelValue once.
 */
watch(
  () => props.modelValue,
  (value) => {
    inputModel.value = value;
    field.value = value;
  },
);

watch(
  () => props.invalid,
  () => {
    field.validate?.();
  },
);
</script>

<template>
  <FieldWrapper
    :label="props.label"
    :label-class="props.labelClass"
    :mandatory="props.mandatory"
  >
    <InputGroup :class="props.inputContainerClass" :invalid="invalidInput">
      <Password
        v-model="inputModel"
        :feedback="props.feedback"
        :invalid="invalidInput"
        :placeholder="props.placeholder"
        @update:model-value="updateFieldValue"
        toggle-mask
      >
        <template #showicon="{ toggleCallback }">
          <button
            :class="Preset?.showicon.class"
            @click="toggleCallback(), focusInput($event)"
            class="unset"
          >
            <Icon icon="eye" />
          </button>
        </template>

        <template #hideicon="{ toggleCallback }">
          <button
            :class="Preset?.showicon.class"
            @click="toggleCallback(), focusInput($event)"
            class="unset"
          >
            <Icon icon="eye-off" />
          </button>
        </template>
      </Password>
    </InputGroup>

    <ValidatorMessage
      :class="props.validatorMessageClass"
      :message="field.errorMessage"
    />
  </FieldWrapper>
</template>
